/**
 * CompleteProfileScreen
 * Shown after onboarding to let users complete their profile
 */

import React, { useState } from "react";
import {
  View,
  Text,
  ScrollView,
  TextInput,
  TouchableOpacity,
  Platform,
  KeyboardAvoidingView,
  ActivityIndicator,
  Alert,
} from "react-native";
import { SafeAreaView, useSafeAreaInsets } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import DateTimePicker from "@react-native-community/datetimepicker";
import LottieView from "lottie-react-native";
import { useThemeColors } from "@/providers/ThemeProvider";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/lib/supabase/client";

interface CompleteProfileScreenProps {
  onComplete: () => void;
  onSkip: () => void;
}

export const CompleteProfileScreen: React.FC<CompleteProfileScreenProps> = ({
  onComplete,
  onSkip,
}) => {
  const colors = useThemeColors();
  const insets = useSafeAreaInsets();
  const { user, activeProfile, refreshProfile } = useAuth();
  const userId = activeProfile?.id || user?.id;

  // Form state - pre-fill with existing data
  const [nickname, setNickname] = useState(activeProfile?.nickname || "");
  const [phone, setPhone] = useState(activeProfile?.phone || "");
  const [dateOfBirth, setDateOfBirth] = useState<Date | null>(
    activeProfile?.date_of_birth ? new Date(activeProfile.date_of_birth) : null
  );
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  /**
   * Check if profile has any new data to save
   */
  const hasChanges = () => {
    return (
      nickname.trim() !== (activeProfile?.nickname || "") ||
      phone.trim() !== (activeProfile?.phone || "") ||
      (dateOfBirth && dateOfBirth.toISOString().split("T")[0] !== activeProfile?.date_of_birth)
    );
  };

  /**
   * Save profile and complete
   */
  const handleSave = async () => {
    if (!userId) return;

    setIsSaving(true);

    try {
      const updates: Record<string, any> = {
        updated_at: new Date().toISOString(),
      };

      if (nickname.trim()) {
        updates.nickname = nickname.trim();
      }
      if (phone.trim()) {
        updates.phone = phone.trim();
      }
      if (dateOfBirth) {
        updates.date_of_birth = dateOfBirth.toISOString().split("T")[0];
      }

      const { error } = await supabase
        .from("profiles")
        .update(updates)
        .eq("id", userId);

      if (error) throw error;

      // Refresh profile data
      await refreshProfile();

      onComplete();
    } catch (error) {
      console.error("Error saving profile:", error);
      Alert.alert("Fout", "Kon profiel niet opslaan. Probeer het later opnieuw.");
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Handle skip - can complete later
   */
  const handleSkip = () => {
    onSkip();
  };

  return (
    <SafeAreaView
      className="flex-1"
      style={{ backgroundColor: colors.background }}
      edges={["top"]}
    >
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        style={{ flex: 1 }}
      >
        {/* Header with skip button */}
        <View className="flex-row items-center justify-between px-4 py-2">
          <View style={{ width: 60 }} />
          <Text className="text-lg font-semibold" style={{ color: colors.text }}>
            Profiel aanvullen
          </Text>
          <TouchableOpacity
            onPress={handleSkip}
            className="px-3 py-2"
            disabled={isSaving}
          >
            <Text style={{ color: colors.primary }}>Later</Text>
          </TouchableOpacity>
        </View>

        <ScrollView
          className="flex-1 px-6"
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          {/* Welcome message */}
          <View className="items-center py-6">
            <View className="w-32 h-32 mb-4">
              <LottieView
                source={require("@/assets/lottie/Personal_input.json")}
                autoPlay
                loop
                style={{ width: 128, height: 128 }}
              />
            </View>
            <Text
              className="text-2xl font-bold text-center"
              style={{ color: colors.text }}
            >
              Welkom, {activeProfile?.full_name?.split(" ")[0] || ""}! 👋
            </Text>
            <Text
              className="text-center mt-2 px-4"
              style={{ color: colors.textSecondary }}
            >
              Maak je profiel compleet zodat andere familieleden je makkelijker kunnen vinden.
            </Text>
          </View>

          {/* Info box */}
          <View
            className="rounded-xl p-4 mb-6 flex-row items-start"
            style={{ backgroundColor: colors.primaryLight }}
          >
            <Ionicons name="information-circle" size={20} color={colors.primary} />
            <Text className="ml-3 flex-1 text-sm" style={{ color: colors.text }}>
              Deze stap is optioneel. Je kunt dit later altijd nog aanpassen in je profiel.
            </Text>
          </View>

          {/* Nickname */}
          <View className="mb-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.text }}>
              Bijnaam
            </Text>
            <TextInput
              value={nickname}
              onChangeText={setNickname}
              placeholder="Hoe noemen anderen je?"
              placeholderTextColor={colors.textTertiary}
              autoCapitalize="words"
              className="rounded-xl px-4 py-3"
              style={{
                backgroundColor: colors.surface,
                color: colors.text,
                borderWidth: 1,
                borderColor: colors.border,
              }}
            />
            <Text className="text-xs mt-1" style={{ color: colors.textSecondary }}>
              Bijv. "Opa Peter", "Tante Marie", of je roepnaam
            </Text>
          </View>

          {/* Phone */}
          <View className="mb-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.text }}>
              Telefoonnummer
            </Text>
            <TextInput
              value={phone}
              onChangeText={setPhone}
              placeholder="+31 6 12345678"
              placeholderTextColor={colors.textTertiary}
              keyboardType="phone-pad"
              className="rounded-xl px-4 py-3"
              style={{
                backgroundColor: colors.surface,
                color: colors.text,
                borderWidth: 1,
                borderColor: colors.border,
              }}
            />
          </View>

          {/* Date of Birth */}
          <View className="mb-6">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.text }}>
              Geboortedatum
            </Text>
            <TouchableOpacity
              onPress={() => setShowDatePicker(true)}
              className="rounded-xl px-4 py-3 flex-row items-center justify-between"
              style={{
                backgroundColor: colors.surface,
                borderWidth: 1,
                borderColor: colors.border,
              }}
            >
              <Text style={{ color: dateOfBirth ? colors.text : colors.textTertiary }}>
                {dateOfBirth
                  ? dateOfBirth.toLocaleDateString("nl-NL", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })
                  : "Selecteer je verjaardag"}
              </Text>
              <Ionicons name="calendar-outline" size={20} color={colors.textSecondary} />
            </TouchableOpacity>
            <Text className="text-xs mt-1" style={{ color: colors.textSecondary }}>
              Zo kan iedereen je verjaardag niet vergeten! 🎂
            </Text>
          </View>

          {showDatePicker && (
            <DateTimePicker
              value={dateOfBirth || new Date(1990, 0, 1)}
              mode="date"
              display={Platform.OS === "ios" ? "spinner" : "default"}
              onChange={(event, selectedDate) => {
                setShowDatePicker(Platform.OS === "ios");
                if (selectedDate) {
                  setDateOfBirth(selectedDate);
                }
              }}
              maximumDate={new Date()}
            />
          )}

          {/* Save Button */}
          <TouchableOpacity
            onPress={handleSave}
            disabled={isSaving}
            className="rounded-xl py-4 items-center mb-4"
            style={{
              backgroundColor: isSaving ? colors.textSecondary : colors.primary,
              opacity: isSaving ? 0.7 : 1,
            }}
          >
            {isSaving ? (
              <ActivityIndicator color="#FFFFFF" />
            ) : (
              <Text className="text-white font-semibold text-base">
                {hasChanges() ? "Opslaan" : "Doorgaan"}
              </Text>
            )}
          </TouchableOpacity>

          {/* Skip link */}
          <TouchableOpacity
            onPress={handleSkip}
            disabled={isSaving}
            className="items-center py-4 mb-8"
          >
            <Text style={{ color: colors.textSecondary }}>
              Ik doe dit later wel
            </Text>
          </TouchableOpacity>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

export default CompleteProfileScreen;

